<template>
  <div
    class="yxtbiz-ai-robot"
    :class="{ 'is-fullscreen': bFullscreen, 'is-max': max }"
  >
    <draggable-container
      v-loading="!scriptLoaded"
      yxt-loading-background="rgba(255, 255, 255, 0)"
      class="yxtbiz-ai-robot--container"
      :bFullscreen="bFullscreen"
      @resize="() => $refs.msgDialog.scrollToEnd()"
    >
    <template v-if="scriptLoaded">
      <broadside
        :visible.sync="chatListVisible"
        :title="title"
        :waitAnswer="waitAnswer"
        :small-client="!bFullscreen"
        @selectChat="selectChat"
      ></broadside>
      <msg-dialog
        ref="msgDialog"
        :class="{mr320: bFullscreen && showTreasure, ml280: bFullscreen && chatListVisible}"
        :waitAnswer.sync="waitAnswer"
        :clientType="getClientType"
        :title="title"
        :max="max"
        :hideHeadToolbar="bFullscreen && showTreasure"
        :bFullscreen="bFullscreen"
        @getChatSetting="getChatSetting"
        @fullscreen="fullscreenChange"
        @close="closeAI"
        @showChatList="showChatList"
      ></msg-dialog>
      <treasureDialog :visible.sync="showTreasure"/>
    </template>
    </draggable-container>
  </div>
</template>

<script>
import { getChatSetting, getStaticBaseUrl } from './service';
import MsgDialog from './components/msgDialog.vue';
import DraggableContainer from './components/draggableContainer.vue';
import broadside from './components/broadside.vue';
import { loadScript } from 'yxt-biz-pc/src/utils/util';
import { getGlobalLangTrans } from './service';
import utils from 'yxt-biz-pc/packages/common-util/index.js';
import { addClass, removeClass } from 'yxt-biz-pc/src/utils/dom';
import treasureDialog from './components/treasureDialog.vue';

export default {
  name: 'YxtbizAiRobot',
  name_zh: '企业AI助手',
  eventList: [{
    type: 'close',
    handler: 'close',
    label: '关闭弹窗'
  }],
  components: {
    broadside,
    MsgDialog,
    DraggableContainer,
    treasureDialog
  },
  provide() {
    return {
      settings: this.settings,
      clientType: this.mode,
      textLang: this.textLang,
      getClient: () => this.getClientType,
      getFullscreen: () => this.bFullscreen
    };
  },
  props: {
    mode: {
      type: String,
      default: 'manage',
      validator: val => ['manage', 'student'].includes(val),
      desc: '使用端',
      enums: [{
        label: '管理端',
        value: 'manage'
      }, {
        label: '学员端',
        value: 'student'
      }]
    },
    max: {
      type: Boolean,
      default: false,
      desc: '可否最大宽高',
      setter: 'BooleanSetter'
    },
    title: {
      type: String,
      default() {
        return this.$t('pc_biz_ai_title').d('企业AI助手');
      },
      desc: '组件标题',
      setter: 'String'
    }
  },
  computed: {
    getClientType() {
      // 0-学员端，1-管理端
      return this.mode === 'manage' ? 1 : 0;
    }
  },
  data() {
    return {
      scriptLoaded: false,
      textLang: {
        compulsoryText: '',
        electiveText: ''
      },
      bFullscreen: false,
      settings: {
        answerInteract: '', // 提供反馈路径
        publicResourceEnabled: false, // 是否开通互联网
        factorOpenFlag: false, // 要素是否开通
        assistEnabled: false, // 是否开启
        accessEnabled: false, // 是否在使用范围内
        assistHeadImg: `${getStaticBaseUrl()}ufd/407a24/assistent/pc/img/avatar.jpg`,
        assistWele: '', // 助手欢迎语
        hasShortcutInstruction: null, // ("是否包含快捷指令，1-有，0-无")
        fastFeedbackList: [] // 反馈提示词
      },
      waitAnswer: false, // 是否等待回答
      chatListVisible: false, // 多窗口列表显隐
      showTreasure: false
    };
  },
  watch: {
    bFullscreen(val) {
      if (val) {
        addClass(document.body, 'yxt-popup-parent--hidden');
      } else {
        removeClass(document.body, 'yxt-popup-parent--hidden');
      }
    }
  },
  created() {
    this.bFullscreen = !!this.max;
    this.loadScript();
    this.getChatSetting();
    this.getDefinedLang();
    this.$root.$on('SHOW_TREASURE', this.handleToggleTreasure);
  },
  beforeDestroy() {
    removeClass(document.body, 'yxt-popup-parent--hidden');
    this.$root.$off('SHOW_TREASURE', this.handleToggleTreasure);
  },
  methods: {
    getDefinedLang() {
      const LANGKEY = {
        O2O_COMPULSORY: 'keys.org.ssp.custom.global.compulsory', // 必修文案
        O2O_OPTIONAL: 'keys.org.ssp.custom.global.optional' // 选修文案
      };
      getGlobalLangTrans(LANGKEY.O2O_OPTIONAL).then(data => {
        this.textLang.compulsoryText = data && data[utils.getLanguage()] || this.$t('pc_o2o_lbl_elective');
      }).catch(() => {
      });
      getGlobalLangTrans(LANGKEY.O2O_COMPULSORY).then(data => {
        this.textLang.electiveText = data && data[utils.getLanguage()] || this.$t('pc_o2o_lbl_obligatory');
      }).catch(() => {
      });
    },
    loadScript() {
      this.scriptLoaded = false;
      const url = `${this.$staticBaseUrl}ufd/55a3e0/js/marked.min.js`;
      loadScript(url, true)
        .finally(() => {
          this.scriptLoaded = true;
        });
    },
    // 点击窗口回调
    selectChat(item, isNew) {
      // 没有chatSessionId则为新窗口
      if (!this.bFullscreen) this.chatListVisible = false;
      this.$refs.msgDialog.changeUpdateAI(item.chatSessionId || '', isNew);
    },
    // 展示多窗口组件
    showChatList() {
      this.chatListVisible = true;
    },
    // 获取AI助手配置
    getChatSetting() {
      getChatSetting().then(res => {
        if (res) {
          this.settings.answerInteract = !!res.answerInteract;
          this.settings.assistEnabled = !!res.assistEnabled;
          this.settings.factorOpenFlag = !!res.factorOpenFlag;
          this.settings.accessEnabled = !!res.accessEnabled;
          this.settings.publicResourceEnabled = !!res.publicResourceEnabled;
          this.settings.assistHeadImg = res.assistHeadImg || `${getStaticBaseUrl()}ufd/407a24/assistent/pc/img/avatar.jpg`;
          this.settings.assistWele = res.assistWele;
          this.settings.fastFeedbackList = res.fastFeedbackList || [];
          this.settings.hasShortcutInstruction = res.hasShortcutInstruction;
        }
      });
    },
    closeAI() {
      this.$emit('close');
    },
    handleToggleTreasure(visible) {
      if (visible === undefined) {
        this.showTreasure = !this.showTreasure;
      } else {
        this.showTreasure = visible;
      }
    },
    fullscreenChange(val) {
      this.chatListVisible = val;
      this.bFullscreen = val;
    }
  }
};
</script>
